'use client'
import { AnimatePresence, motion } from 'framer-motion';
import { useState, useEffect } from "react";
import { createPortal } from 'react-dom';
import { useLanguage } from "@/contexts/I18nProvider";
import { useSector } from "@/contexts/SectorContext";
import { plusJakartaSans } from "@/app/layout";
import contactService from "@/templates/Starglobal3D/utils/services/contact";
import { Sector } from "@/types";
import { useTranslations } from "next-intl";


import { isLocale } from '@/utils/constant/locales';

const getLocalizedUrl = (url: string, locale: string) => {
    if (!url) return '';
    
    if (url.startsWith('http://') || url.startsWith('https://')) {
        try {
            const parsed = new URL(url);
            if (parsed.hostname.includes('starglobal3d.com')) {
                let pathParts = parsed.pathname.split('/').filter(Boolean);
                if (isLocale(pathParts[0])) {
                    pathParts.shift();
                }
                if (locale && locale !== 'vn') {
                    pathParts.unshift(locale);
                }
                parsed.pathname = '/' + pathParts.join('/');
                return parsed.toString();
            }
        } catch (e) {
            // fallback
        }
    } else if (url.startsWith('/')) {
        let pathParts = url.split('/').filter(Boolean);
        if (isLocale(pathParts[0])) {
            pathParts.shift();
        }
        if (locale && locale !== 'vn') {
            pathParts.unshift(locale);
        }
        return '/' + pathParts.join('/');
    }
    return url;
};

export default function ContactButton() {
    const { locale } = useLanguage();
    const { sectors } = useSector();
    const t = useTranslations();
    const [isContact, setIsContact] = useState<boolean>(false);
    const [mounted, setMounted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        company: '',
        service: '',
        sector: '',
        content: '',
        brochure_url: '',
    });
    const [isServiceDropdownOpen, setIsServiceDropdownOpen] = useState(false);
    const [isSectorDropdownOpen, setIsSectorDropdownOpen] = useState(false);
    const [translatedSectors, setTranslatedSectors] = useState<(Sector & { nameTranslate?: string })[]>([]);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('global_contact_state', { detail: { isOpen: isContact } }));
        }
    }, [isContact]);

    const handleContactClick = () => {
        setIsContact(true);
    };

    const handleClose = () => {
        setIsContact(false);
        // Reset after close animation
        setTimeout(() => {
            setSubmitStatus('idle');
            setErrorMessage('');
            setFormData({ name: '', phone: '', email: '', company: '', service: '', sector: '', content: '', brochure_url: '' });
            setIsServiceDropdownOpen(false);
            setIsSectorDropdownOpen(false);
        }, 300);
    };

    const getBrowserMeta = () => {
        const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
        const lower = ua.toLowerCase();

        let browser = 'unknown';
        if (lower.includes('edg/')) browser = 'edge';
        else if (lower.includes('chrome')) browser = 'chrome';
        else if (lower.includes('safari')) browser = 'safari';
        else if (lower.includes('firefox')) browser = 'firefox';

        let platform = 'unknown';
        if (lower.includes('windows')) platform = 'windows';
        else if (lower.includes('mac')) platform = 'macos';
        else if (lower.includes('iphone') || lower.includes('ipad')) platform = 'ios';
        else if (lower.includes('android')) platform = 'android';

        return { browser, platform };
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSubmitting) return;

        setIsSubmitting(true);
        setSubmitStatus('idle');
        setErrorMessage('');

        try {
            const { browser, platform } = getBrowserMeta();
            const submitData = {
                ...formData,
                url_web: typeof window !== 'undefined' ? window.location.href : '',
                browser_meta: `${browser} on ${platform}`,
            };

            const response = await contactService.submitContact(submitData);
            if (response.success) {
                setSubmitStatus('success');
                setFormData({ name: '', phone: '', email: '', company: '', service: '', sector: '', content: '', brochure_url: '' });
            } else {
                setSubmitStatus('error');
                setErrorMessage(response.message || (locale === 'en' ? 'Send failed. Please try again.' : locale === 'ja' ? '送信に失敗しました。もう一度お試しください。' : 'Gửi thất bại. Vui lòng thử lại.'));
            }
        } catch (error) {
            console.error('Submit contact error:', error);
            setSubmitStatus('error');
            setErrorMessage(locale === 'en' ? 'An error occurred. Please try again.' : locale === 'ja' ? 'エラーが発生しました。もう一度お試しください。' : 'Đã xảy ra lỗi. Vui lòng thử lại.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const modal = (
        <AnimatePresence>
            {isContact && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'rgba(0,0,0,0.5)',
                        backdropFilter: 'blur(4px)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        padding: '16px',
                    }}
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 15 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 15 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        className={`w-full max-w-[850px] bg-[#1e1e1e] rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.3)] border border-white/10 p-6 md:p-8 flex flex-col relative max-h-[90vh] overflow-y-auto ${plusJakartaSans.className}`}
                        style={{
                            boxSizing: 'border-box',
                        }}
                    >
                        {/* Header Row: Title and Close button on the same line */}
                        <div className="flex justify-between items-center mb-4 md:mb-6">
                            <h2 style={{ fontSize: '24px', fontWeight: 700 }} className="md:text-[26px]">
                                {locale === 'en' ? 'Contact' : locale === 'ja' ? 'お問い合わせ' : 'Liên hệ'}
                            </h2>
                            <button
                                onClick={handleClose}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#fff',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    lineHeight: 1,
                                    opacity: 0.7,
                                    transition: 'opacity 0.15s',
                                    padding: '4px',
                                }}
                                onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                                onMouseLeave={e => (e.currentTarget.style.opacity = '0.7')}
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
                            {/* LEFT — Form */}
                            <div>
                                <p style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>
                                    {locale === 'en' ? 'Do you need digital solution consulting?' : locale === 'ja' ? 'デジタルソリューションのご相談が必要ですか？' : 'Bạn cần tư vấn giải pháp số hóa?'}
                                </p>
                                <p style={{ fontSize: '13px', color: '#bbb', marginBottom: '22px', lineHeight: 1.65 }}>
                                    {locale === 'en'
                                        ? 'Our Star Global 3D experts are always ready to listen and answer your questions. Leave your information now to receive a response within 24 working hours.'
                                        : locale === 'ja'
                                        ? 'Star Global 3Dの専門家チームは、いつでもお客様の質問にお答えする準備ができています。24営業時間以内に返信いたしますので、今すぐ情報をご入力ください。'
                                        : 'Đội ngũ chuyên gia của Star Global 3D luôn sẵn sàng lắng nghe và giải đáp mọi thắc mắc của bạn. Để lại thông tin ngay để nhận được phản hồi trong vòng 24h làm việc.'}
                                </p>

                                {/* Mobile contact info - hidden on desktop */}
                                <div className="md:hidden flex flex-col gap-4 mb-6">
                                    {/* Row 1: Liên hệ tư vấn */}
                                    <div>
                                        <p style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>
                                            {locale === 'en' ? 'Contact for consultation' : locale === 'ja' ? '相談のお問い合わせ' : 'Liên hệ tư vấn'}
                                        </p>
                                        <div className="flex flex-row justify-start gap-6 pl-2">
                                        <a href="https://zalo.me/923079094077439298" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1">
                                            <img src="/images/social-icons/zalo.png" alt="Zalo" className="w-8 h-8 object-contain" />
                                            <span style={{ fontSize: '10px', color: '#ccc' }}>Zalo</span>
                                        </a>
                                        <a href="https://www.messenger.com/login.php?next=https%3A%2F%2Fwww.messenger.com%2Ft%2F354403448245531%2F%3Fref%3D7zRjRGVpgXN7%26messaging_source%3Dsource%253Apages%253Amessage_shortlink%26source_id%3D1441792%26recurring_notification%3D0" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1">
                                            <img src="/images/social-icons/mess.png" alt="Messenger" className="w-8 h-8 object-contain" />
                                            <span style={{ fontSize: '10px', color: '#ccc' }}>Messenger</span>
                                        </a>
                                        <a href="tel:+84944850527" className="flex flex-col items-center gap-1">
                                            <img src="/images/social-icons/call.png" alt="Hotline" className="w-8 h-8 object-contain" />
                                            <span style={{ fontSize: '10px', color: '#ccc' }}>Hotline</span>
                                        </a>
                                    </div>
                                    </div>

                                    {/* Row 2: Email & Hotline */}
                                    <div className="flex flex-row gap-4">
                                        <div className="flex-1">
                                            <p style={{ fontSize: '13px', fontWeight: 700, marginBottom: '2px' }}>Email:</p>
                                            <p style={{ fontSize: '12px', color: '#ccc' }}>ask@starglobal3d.com</p>
                                            <p style={{ fontSize: '12px', color: '#ccc' }}>info@starglobal3d.com</p>
                                            <p style={{ fontSize: '12px', color: '#ccc' }}>contact@starglobal3d.com</p>
                                        </div>
                                        <div className="flex-1">
                                            <p style={{ fontSize: '13px', fontWeight: 700, marginBottom: '2px' }}>Hotline:</p>
                                            <p style={{ fontSize: '12px', color: '#ccc' }}>VN: (+84) 944 850 527</p>
                                            <p style={{ fontSize: '12px', color: '#ccc' }}>AUS: (+61) 468 313 685</p>
                                        </div>
                                    </div>

                                    {/* Row 3: Website & Social */}
                                    <div className="flex flex-row gap-4">
                                        <div className="flex-1">
                                            <p style={{ fontSize: '13px', fontWeight: 700, marginBottom: '2px' }}>Website:</p>
                                            <a href="https://starglobal3d.com/" target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#ffffffff' }}>starglobal3d.com</a>
                                        </div>
                                        <div className="flex-1">
                                            <p style={{ fontSize: '13px', fontWeight: 700, marginBottom: '2px' }}>{locale === 'ja' ? 'SNS' : locale === 'en' ? 'Social media' : 'Mạng xã hội'}:</p>
                                            <div className="flex flex-row gap-2 mt-1">
                                                <a href="https://www.facebook.com/StarGlobal3D" target="_blank" rel="noopener noreferrer"><img src="/images/social-icons/face.png" alt="Facebook" className="w-5 h-5 object-contain" /></a>
                                                <a href="https://www.linkedin.com/company/starglobal3d/posts/?feedView=all" target="_blank" rel="noopener noreferrer"><img src="/images/social-icons/linkedin.png" alt="LinkedIn" className="w-5 h-5 object-contain" /></a>
                                                <a href="https://www.youtube.com/@StarGlobal_3D" target="_blank" rel="noopener noreferrer"><img src="/images/social-icons/youtube.png" alt="YouTube" className="w-5 h-5 object-contain" /></a>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {[
                                        { label: locale === 'en' ? 'Full Name *' : locale === 'ja' ? '氏名 *' : 'Họ & Tên *', name: 'name', required: true },
                                        { label: locale === 'en' ? 'Phone number *' : locale === 'ja' ? '電話番号 *' : 'Số điện thoại *', name: 'phone', required: true },
                                        { label: 'Email *', name: 'email', required: true },
                                        { label: locale === 'en' ? 'Organization / Company' : locale === 'ja' ? '所属・会社名' : 'Tên đơn vị / tổ chức', name: 'company', required: false },
                                        { label: locale === 'en' ? 'Consultation service *' : locale === 'ja' ? '相談サービス *' : 'Dịch vụ cần tư vấn *', name: 'service', required: true },
                                    ].map(field => (
                                        <div key={field.name} className="flex items-center gap-2 w-full min-w-0">
                                            <div className="w-[115px] sm:w-[155px] shrink-0">
                                                <label className="text-[12px] sm:text-[13px] text-white whitespace-normal leading-tight block">{field.label}</label>
                                            </div>
                                            <div className="flex-1 min-w-0 relative">
                                            {field.name === 'service' ? (
                                                <>
                                                    <div
                                                        className="w-full text-[13px] text-black bg-white rounded p-[7px_30px_7px_10px] outline-none truncate cursor-pointer select-none ring-1 ring-inset ring-gray-100"
                                                        onClick={() => setIsServiceDropdownOpen(!isServiceDropdownOpen)}
                                                    >
                                                        {formData.service || (locale === 'en' ? '-- Select --' : locale === 'ja' ? '-- 選択 --' : '-- Chọn --')}
                                                    </div>
                                                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                                        <svg className={`w-4 h-4 text-gray-500 transition-transform ${isServiceDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                                    </div>

                                                    {/* Hidden input for form validation */}
                                                    <input
                                                        type="text"
                                                        name="service"
                                                        required={true}
                                                        value={formData.service}
                                                        className="opacity-0 absolute bottom-0 left-1/2 -z-10 w-0 h-0"
                                                        onChange={() => {}}
                                                        tabIndex={-1}
                                                    />

                                                    {/* Custom Dropdown Option List */}
                                                    {isServiceDropdownOpen && (
                                                        <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded shadow-lg z-50 overflow-y-auto border border-gray-200" style={{ maxHeight: '200px' }}>
                                                            {(locale === 'en' ? [
                                                                '3D scanning service',
                                                                '3D object digitization',
                                                                '3D/360 space digitization',
                                                                '3D/360 data processing',
                                                                '3D/360 digital mapping by UAV',
                                                                '3D/360 smart spatial management system'
                                                            ] : locale === 'ja' ? [
                                                                '3Dスキャンサービス',
                                                                '3Dオブジェクトのデジタル化',
                                                                '3D/360空間のデジタル化',
                                                                '3D/360データ処理',
                                                                'UAVによる3D/360デジタルマッピング',
                                                                '3D/360スマート空間管理システム'
                                                            ] : [
                                                                'Dịch vụ scan 3D',
                                                                'Số hóa 3D vật thể',
                                                                'Số hóa không gian 3D/360',
                                                                'Gia công xử lý dữ liệu 3D/360',
                                                                'Xây dựng bản đồ số 3D/360 bằng UAV',
                                                                'Hệ thống quản lý thông minh không gian số 3D/360'
                                                            ]).map((opt, idx) => (
                                                                <div
                                                                    key={idx}
                                                                    className="px-3 py-2 text-[13px] hover:bg-gray-100 cursor-pointer text-black border-b border-gray-100 last:border-b-0 whitespace-normal break-words leading-snug"
                                                                    onClick={() => {
                                                                        setFormData(prev => ({ ...prev, service: opt }));
                                                                        setIsServiceDropdownOpen(false);
                                                                    }}
                                                                >
                                                                    {opt}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <input
                                                    type="text"
                                                    name={field.name}
                                                    required={field.required}
                                                    value={formData[field.name as keyof typeof formData]}
                                                    onChange={handleChange}
                                                    className="w-full text-[13px] text-black bg-white rounded p-[7px_10px] outline-none"
                                                />
                                            )}
                                            </div>
                                        </div>
                                    ))}

                                    {/* Lĩnh vực quan tâm */}
                                    <div className="flex items-center gap-2 w-full min-w-0 mb-2">
                                        <div className="w-[115px] sm:w-[155px] shrink-0">
                                            <label className="text-[12px] sm:text-[13px] text-white whitespace-normal leading-tight block">
                                                {locale === 'en' ? 'Field of interest' : locale === 'ja' ? '関心分野' : 'Lĩnh vực quan tâm'}
                                            </label>
                                        </div>
                                        <div className="flex-1 min-w-0 relative">
                                            <div
                                                className="w-full text-[13px] text-black bg-white rounded p-[7px_30px_7px_10px] outline-none truncate cursor-pointer select-none ring-1 ring-inset ring-gray-100"
                                                onClick={() => setIsSectorDropdownOpen(!isSectorDropdownOpen)}
                                            >
                                                {formData.sector || (locale === 'en' ? '-- Select --' : locale === 'ja' ? '-- 選択 --' : '-- Chọn --')}
                                            </div>
                                            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                                <svg className={`w-4 h-4 text-gray-500 transition-transform ${isSectorDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                            </div>
                                            {isSectorDropdownOpen && (
                                                <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded shadow-lg z-50 overflow-y-auto border border-gray-200" style={{ maxHeight: '200px' }}>
                                                    {[...sectors].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)).map(sector => {
                                                        const label = locale === 'en' ? sector.display_en?.trim() : locale === 'ja' ? (sector.display_ja?.trim() || sector.display_en?.trim()) : sector.display_vn?.trim() || sector.display_en?.trim();
                                                        if (!label) return null;
                                                        return (
                                                            <div
                                                                key={sector.id_field}
                                                                className="px-3 py-2 text-[13px] hover:bg-gray-100 cursor-pointer text-black border-b border-gray-100 last:border-b-0 whitespace-normal break-words leading-snug"
                                                                onClick={() => {
                                                                    setFormData(prev => ({ ...prev, sector: label, brochure_url: getLocalizedUrl(sector.brochure_url || '', locale) }));
                                                                    setIsSectorDropdownOpen(false);
                                                                }}
                                                            >
                                                                    {label}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Textarea */}
                                    <div className="flex items-start gap-2 w-full min-w-0">
                                        <div className="w-[115px] sm:w-[155px] shrink-0 pt-1 sm:pt-[7px]">
                                            <label className="text-[12px] sm:text-[13px] text-white whitespace-normal leading-tight block">
                                                {locale === 'en' ? 'Support needed *' : locale === 'ja' ? 'サポートが必要な内容 *' : 'Nội dung cần hỗ trợ *'}
                                            </label>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <textarea
                                                name="content"
                                                required
                                                rows={4}
                                                value={formData.content}
                                                onChange={handleChange}
                                                className="w-full text-[13px] text-black bg-white rounded p-[7px_10px] outline-none resize-y"
                                            />
                                        </div>
                                    </div>

                                    {/* Status messages */}
                                    {submitStatus === 'success' && (
                                        <div style={{
                                            background: 'rgba(34,197,94,0.12)',
                                            border: '1px solid rgba(34,197,94,0.4)',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            marginTop: '10px',
                                        }}>
                                            <span style={{ fontSize: '20px' }}>✅</span>
                                            <div>
                                                <p style={{ fontWeight: 700, fontSize: '14px', color: '#4ade80', marginBottom: '2px' }}>
                                                    {locale === 'en' ? 'Sent successfully!' : locale === 'ja' ? '送信に成功しました！' : 'Gửi thành công!'}
                                                </p>
                                                <p style={{ fontSize: '12px', color: '#86efac' }}>
                                                    {locale === 'en'
                                                        ? 'We will get back to you within 24 working hours.'
                                                        : locale === 'ja'
                                                        ? '24営業時間以内にご返信いたします。'
                                                        : 'Chúng tôi sẽ phản hồi bạn trong vòng 24h làm việc.'}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    {submitStatus === 'error' && (
                                        <div style={{
                                            background: 'rgba(239,68,68,0.12)',
                                            border: '1px solid rgba(239,68,68,0.4)',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            marginTop: '10px',
                                        }}>
                                            <span style={{ fontSize: '20px' }}>⚠️</span>
                                            <p style={{ fontSize: '13px', color: '#fca5a5' }}>
                                                {errorMessage || (locale === 'en' ? 'Send failed. Please try again.' : locale === 'ja' ? '送信に失敗しました。もう一度お試しください。' : 'Gửi thất bại. Vui lòng thử lại.')}
                                            </p>
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                                        {submitStatus === 'success' ? (
                                            <button
                                                type="button"
                                                onClick={handleClose}
                                                style={{
                                                    background: '#4ade80',
                                                    color: '#000',
                                                    fontWeight: 700,
                                                    fontSize: '14px',
                                                    padding: '10px 36px',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    letterSpacing: '0.5px',
                                                }}
                                            >
                                                {locale === 'en' ? 'CLOSE' : locale === 'ja' ? '閉じる' : 'ĐÓNG'}
                                            </button>
                                        ) : (
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                style={{
                                                    background: isSubmitting ? '#b3a800' : '#FFF500',
                                                    color: '#000',
                                                    fontWeight: 700,
                                                    fontSize: '14px',
                                                    padding: '10px 48px',
                                                    border: 'none',
                                                    borderRadius: '0',
                                                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                                    letterSpacing: '0.5px',
                                                    transition: 'background 0.15s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    opacity: isSubmitting ? 0.8 : 1,
                                                }}
                                                onMouseEnter={e => { if (!isSubmitting) e.currentTarget.style.background = '#e6dd00'; }}
                                                onMouseLeave={e => { if (!isSubmitting) e.currentTarget.style.background = '#FFF500'; }}
                                            >
                                                {isSubmitting && (
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                                        style={{ animation: 'spin 0.8s linear infinite' }}>
                                                        <circle cx="12" cy="12" r="10" stroke="#000" strokeWidth="3" strokeOpacity="0.25" />
                                                        <path d="M12 2a10 10 0 0 1 10 10" stroke="#000" strokeWidth="3" strokeLinecap="round" />
                                                    </svg>
                                                )}
                                                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                                                {isSubmitting
                                                    ? (locale === 'en' ? 'SENDING...' : locale === 'ja' ? '送信中...' : 'ĐANG GỬI...')
                                                    : (locale === 'en' ? 'SEND' : locale === 'ja' ? '送信' : 'GỬI')}
                                            </button>
                                        )}
                                    </div>
                                </form>
                            </div>

                            {/* RIGHT — Contact Info (Desktop only) */}
                            <div className="hidden md:flex flex-col gap-[22px] pt-[52px]">
                                {/* Liên hệ tư vấn */}
                                <div>
                                    <p style={{ fontSize: '18px', fontWeight: 700, marginBottom: '12px' }}>
                                        {locale === 'en' ? 'Contact for consultation' : locale === 'ja' ? '相談のお問い合わせ' : 'Liên hệ tư vấn'}
                                    </p>
                                    <div className="flex flex-row gap-6 pl-2">
                                        <a href="https://zalo.me/923079094077439298" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-2">
                                            <img src="/images/social-icons/zalo.png" alt="Zalo" className="w-10 h-10 object-contain" />
                                            <span style={{ fontSize: '12px', color: '#ccc' }}>Zalo</span>
                                        </a>
                                        <a href="https://www.messenger.com/login.php?next=https%3A%2F%2Fwww.messenger.com%2Ft%2F354403448245531%2F%3Fref%3D7zRjRGVpgXN7%26messaging_source%3Dsource%253Apages%253Amessage_shortlink%26source_id%3D1441792%26recurring_notification%3D0" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-2">
                                            <img src="/images/social-icons/mess.png" alt="Messenger" className="w-10 h-10 object-contain" />
                                            <span style={{ fontSize: '12px', color: '#ccc' }}>Messenger</span>
                                        </a>
                                        <a href="tel:+84944850527" className="flex flex-col items-center gap-2">
                                            <img src="/images/social-icons/call.png" alt="Hotline" className="w-10 h-10 object-contain" />
                                            <span style={{ fontSize: '12px', color: '#ccc' }}>Hotline</span>
                                        </a>
                                    </div>
                                </div>

                                {/* Hotline & Email */}
                                <div>
                                    <p style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>Hotline:</p>
                                    <p style={{ fontSize: '14px', color: '#ccc' }}>VN: (+84) 944 850 527</p>
                                    <p style={{ fontSize: '14px', color: '#ccc' }}>AUS: (+61) 468 313 685</p>
                                    <p style={{ fontSize: '18px', fontWeight: 700, marginTop: '14px', marginBottom: '4px' }}>Email:</p>
                                    <p style={{ fontSize: '14px', color: '#ccc' }}>ask@starglobal3d.com</p>
                                    <p style={{ fontSize: '14px', color: '#ccc' }}>info@starglobal3d.com</p>
                                    <p style={{ fontSize: '14px', color: '#ccc' }}>contact@starglobal3d.com</p>
                                </div>

                                {/* Website */}
                                <div>
                                    <p style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>Website:</p>
                                    <a href="https://starglobal3d.com/" target="_blank" rel="noopener noreferrer" style={{ fontSize: '14px', color: '#ffffffff' }}>starglobal3d.com</a>
                                </div>

                                {/* Mạng xã hội */}
                                <div>
                                    <p style={{ fontSize: '18px', fontWeight: 700, marginBottom: '10px' }}>
                                        {locale === 'en' ? 'Social media' : locale === 'ja' ? 'SNS' : 'Mạng xã hội'}
                                    </p>
                                    <div className="flex flex-row gap-4">
                                        <a href="https://www.facebook.com/StarGlobal3D" target="_blank" rel="noopener noreferrer"><img src="/images/social-icons/face.png" alt="Facebook" className="w-8 h-8 object-contain" /></a>
                                        <a href="https://www.linkedin.com/company/starglobal3d/posts/?feedView=all" target="_blank" rel="noopener noreferrer"><img src="/images/social-icons/linkedin.png" alt="LinkedIn" className="w-8 h-8 object-contain" /></a>
                                        <a href="https://www.youtube.com/@StarGlobal_3D" target="_blank" rel="noopener noreferrer"><img src="/images/social-icons/youtube.png" alt="YouTube" className="w-8 h-8 object-contain" /></a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    return (
        <>
            <div
                className="h-9 px-4 bg-[#FFF500] text-black text-[13px] font-bold hover:bg-[#e6dd00] transition-colors shadow-inner flex items-center justify-center select-none cursor-pointer whitespace-nowrap"
                onClick={handleContactClick}
            >
                {locale === 'en' ? 'CONTACT' : locale === 'ja' ? 'お問い合わせ' : 'LIÊN HỆ'}
            </div>

            {mounted && createPortal(modal, document.body)}
        </>
    );
}
